import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Building2, Clock3, Loader2, Stethoscope, Users } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ClinicAdminLayout from '../components/admin/ClinicAdminLayout';
import { clinicService } from '../services/api';

const ClinicAdminDashboard = () => {
    const { slug: routeSlug } = useParams();
    const slug =
        routeSlug ||
        (() => {
            try {
                return JSON.parse(localStorage.getItem('user') || '{}')?.klinik?.slug;
            } catch {
                return null;
            }
        })();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        poli: 0,
        patients: 0,
        activeQueues: 0,
        doctors: 0,
    });
    const [queueDetail, setQueueDetail] = useState([]);

    useEffect(() => {
        const fetchDashboardStats = async () => {
            if (!slug) {
                setError('Slug klinik tidak ditemukan.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                setError('');
                const response = await clinicService.getDashboardStatsForSlug(slug);
                if (response.data?.success) {
                    setStats({
                        poli: response.data.data?.cards?.poli ?? 0,
                        patients: response.data.data?.cards?.patients ?? 0,
                        activeQueues: response.data.data?.cards?.activeQueues ?? 0,
                        doctors: response.data.data?.cards?.doctors ?? 0,
                    });
                    setQueueDetail(response.data.data?.queueDetail ?? []);
                } else {
                    setError('Gagal memuat statistik dashboard.');
                }
            } catch (err) {
                if (err?.response?.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate(slug ? `/${slug}/login` : '/login');
                    return;
                }
                setError(err?.response?.data?.message || 'Gagal memuat dashboard.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardStats();
    }, [slug, navigate]);

    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayLabel = {
        Monday: 'Sen',
        Tuesday: 'Sel',
        Wednesday: 'Rab',
        Thursday: 'Kam',
        Friday: 'Jum',
        Saturday: 'Sab',
        Sunday: 'Min',
    };

    const chartData = useMemo(() => {
        const byDay = Object.fromEntries(
            dayOrder.map((day) => [day, { day: dayLabel[day], menunggu: 0, dilayani: 0 }]),
        );

        queueDetail.forEach((item) => {
            if (!item?.day || !byDay[item.day]) return;
            byDay[item.day].menunggu = item.waiting ?? 0;
            byDay[item.day].dilayani = item.served ?? 0;
        });

        return dayOrder.map((day) => byDay[day]);
    }, [queueDetail]);

    const cards = [
        {
            key: 'poli',
            label: 'Poli',
            value: stats.poli,
            helper: 'Total poli aktif',
            icon: Building2,
        },
        {
            key: 'patients',
            label: 'Total Patients',
            value: stats.patients,
            helper: 'Jumlah pasien terdaftar',
            icon: Users,
        },
        {
            key: 'activeQueues',
            label: 'Active Queues',
            value: stats.activeQueues,
            helper: 'Antrian berjalan',
            icon: Clock3,
        },
        {
            key: 'doctors',
            label: 'Doctors',
            value: stats.doctors,
            helper: 'Dokter terdaftar',
            icon: Stethoscope,
        },
    ];

    return (
        <ClinicAdminLayout activePage="dashboard" title="Dashboard">
            {loading ? (
                <div className="min-h-[320px] flex items-center justify-center text-slate-500 gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Memuat dashboard...</span>
                </div>
            ) : error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 mt-0.5" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        {cards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <div key={card.key} className="bg-white p-6 rounded-3xl shadow-sm border border-gray-50 flex items-start justify-between relative overflow-hidden group hover:shadow-md transition-all">
                                    <div className="space-y-4 relative z-10">
                                        <p className="text-gray-400 text-sm font-medium">{card.label}</p>
                                        <h2 className="text-3xl font-black text-gray-800 tracking-tight">{card.value}</h2>
                                        <div className="flex items-center gap-2 text-[#00B4D8] text-xs font-bold">
                                            <span className="w-2 h-2 rounded-full bg-[#00B4D8]" />
                                            <span>{card.helper}</span>
                                        </div>
                                    </div>
                                    <div className="w-14 h-14 bg-[#E0F7FA] rounded-2xl flex items-center justify-center text-[#00B4D8] group-hover:scale-110 transition-transform">
                                        <Icon className="w-7 h-7" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="bg-white rounded-3xl shadow-sm border border-gray-50 mb-8 p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold text-gray-800">Detail Antrian</h3>
                        </div>

                        <div className="w-full h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="menungguGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#F4A261" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#F4A261" stopOpacity={0.05} />
                                        </linearGradient>
                                        <linearGradient id="dilayaniGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="#00B4D8" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid strokeDasharray="4 4" stroke="#EEF2F7" />
                                    <XAxis dataKey="day" tick={{ fill: '#94A3B8', fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#94A3B8', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip />

                                    <Area
                                        type="monotone"
                                        dataKey="menunggu"
                                        name="Menunggu"
                                        stroke="#F4A261"
                                        fill="url(#menungguGradient)"
                                        strokeWidth={2.5}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="dilayani"
                                        name="Dilayani"
                                        stroke="#00B4D8"
                                        fill="url(#dilayaniGradient)"
                                        strokeWidth={2.5}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="flex justify-center gap-8 mt-6">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-[#F4A261]" />
                                <span className="text-xs font-bold text-gray-500">Menunggu</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-[#00B4D8]" />
                                <span className="text-xs font-bold text-gray-500">Dilayani</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </ClinicAdminLayout>
    );
};

export default ClinicAdminDashboard;
